/**
 * Crowd Count asset cache — NO service worker.
 *
 * Problem: API returns new pre-signed URLs every time (?X-Amz-...), so the
 * browser HTTP cache always misses. The S3 object path is stable.
 *
 * Fix: key Cache Storage by URL without query params.
 *
 *   signed:  https://…/r9-c13.webp?X-Amz-Signature=…
 *   key:     https://…/r9-c13.webp
 *
 * Flow:
 *   1. Look up key in Cache Storage
 *   2. Hit + fresh (≤7d) → return cached body
 *   3. Miss / expired → fetch signed URL, store under key, return body
 *
 * Images: resolveAssetUrl() turns the body into a blob: URL for <img>.
 * JSON:   cachedFetch() is used by fetchJson().
 *
 * Presigned URLs from the API last ~10min; cached bytes last 7 days.
 */

export const CACHE_NAME = 'crowd-count-assets-v1';
export const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const CACHED_AT_HEADER = 'x-crowd-cached-at';

/** In-tab blob: URLs so we don't recreate them on every render. */
const blobUrls = new Map();

/** origin + pathname only (drop ?X-Amz-…). */
export const toCacheKey = (url) => {
  try {
    const u = new URL(url);
    return `${u.origin}${u.pathname}`;
  } catch {
    return null;
  }
};

/** Any AWS pre-signed URL (any bucket/region) — query has X-Amz-* auth. */
export const isPresignedUrl = (url) => {
  try {
    const { searchParams } = new URL(url);
    return searchParams.has('X-Amz-Signature') || searchParams.has('X-Amz-Algorithm');
  } catch {
    return false;
  }
};

/** HTTPS S3 host (virtual-hosted or path-style). */
export const isS3Hostname = (hostname) => {
  const host = String(hostname || '').toLowerCase();
  if (!host) return false;
  if (host === 's3.amazonaws.com') return true;
  if (/^s3[.-][a-z0-9-]+\.amazonaws\.com$/.test(host)) return true;
  if (host.endsWith('.s3.amazonaws.com')) return true;
  if (/\.s3[.-][a-z0-9-]+\.amazonaws\.com$/.test(host)) return true;
  return false;
};

/** Only fetch HTTPS AWS S3 pre-signed URLs from scan payloads. */
export const isAllowedAssetUrl = (url) => {
  try {
    const u = new URL(url);
    if (u.protocol !== 'https:') return false;
    if (!isS3Hostname(u.hostname)) return false;
    return isPresignedUrl(url);
  } catch {
    return false;
  }
};

const isFresh = (cachedAt) => {
  const t = Number(cachedAt);
  if (!t) return false;
  return Date.now() - t < CACHE_TTL_MS;
};

const isQuotaError = (err) => err
  && (err.name === 'QuotaExceededError' || /quota/i.test(String(err.message || '')));

const evictHalf = async (cache) => {
  const keys = await cache.keys();
  const n = Math.max(1, Math.floor(keys.length / 2));
  await Promise.all(keys.slice(0, n).map((req) => cache.delete(req)));
};

const put = async (cache, key, response) => {
  try {
    await cache.put(key, response);
  } catch (err) {
    if (!isQuotaError(err)) return;
    await evictHalf(cache);
    try {
      await cache.put(key, response);
    } catch {
      // give up caching; caller still has the network response
    }
  }
};

/** Stamp response with cache time so we can enforce TTL on read. */
const stampAndStore = async (cache, key, res) => {
  const headers = new Headers(res.headers);
  headers.set(CACHED_AT_HEADER, String(Date.now()));
  const stamped = new Response(await res.clone().blob(), {
    status: res.status,
    statusText: res.statusText,
    headers,
  });
  await put(cache, key, stamped);
};

const fmtBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

/** Running totals for the current burst; flushed as one summary line. */
const totals = {
  cache: { count: 0, bytes: 0 },
  network: { count: 0, bytes: 0 },
};
let flushTimer = null;

const responseSize = async (res) => {
  const cl = res.headers?.get?.('content-length');
  if (cl && !Number.isNaN(Number(cl))) return Number(cl);
  try {
    return (await res.clone().blob()).size;
  } catch {
    return 0;
  }
};

const flushTotals = () => {
  flushTimer = null;
  const { cache, network } = totals;
  console.log(
    `[crowd-cache] CACHED (no S3 download): ${cache.count} files · ${fmtBytes(cache.bytes)} | `
    + `FRESH (downloaded from S3): ${network.count} files · ${fmtBytes(network.bytes)}`,
  );
};

const record = (kind, bytes) => {
  totals[kind].count += 1;
  totals[kind].bytes += bytes || 0;
  if (flushTimer) clearTimeout(flushTimer);
  // Wait for the load burst to settle, then print one line.
  flushTimer = setTimeout(flushTotals, 600);
};

/** Fetch with path-only Cache Storage. Rejects non-allowlisted URLs. */
export const cachedFetch = async (url) => {
  if (!url || !isAllowedAssetUrl(url)) return null;

  const key = toCacheKey(url);
  if (!key) return null;

  if (typeof caches === 'undefined') {
    return fetch(url);
  }

  const cache = await caches.open(CACHE_NAME);
  const hit = await cache.match(key);
  if (hit) {
    if (isFresh(hit.headers.get(CACHED_AT_HEADER))) {
      record('cache', await responseSize(hit));
      return hit;
    }
    await cache.delete(key);
  }

  const res = await fetch(url);
  if (res.ok) {
    record('network', await responseSize(res));
    await stampAndStore(cache, key, res);
  }
  return res;
};

/**
 * For <img src>: returns a blob: URL backed by Cache Storage.
 * Same signed path → same blob URL for the life of the tab (within TTL).
 */
export const resolveAssetUrl = async (signedUrl) => {
  if (!signedUrl) return null;

  const key = toCacheKey(signedUrl) || signedUrl;
  const existing = blobUrls.get(key);
  if (existing && isFresh(existing.cachedAt)) {
    record('cache', existing.size || 0);
    return existing.url;
  }
  if (existing) {
    try { URL.revokeObjectURL(existing.url); } catch { /* noop */ }
    blobUrls.delete(key);
  }

  const res = await cachedFetch(signedUrl);
  if (!res?.ok) return null;

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const cachedAt = Number(res.headers.get(CACHED_AT_HEADER)) || Date.now();
  blobUrls.set(key, { url: objectUrl, size: blob.size, cachedAt });
  return objectUrl;
};

/** Ask the browser to keep Cache Storage around longer (best-effort). */
export const requestPersistentStorage = async () => {
  try {
    await navigator.storage?.persist?.();
  } catch {
    // ignore
  }
};

/** Remove leftover Crowd Count SW from the earlier approach (if any). */
export const unregisterLegacyAssetSw = async () => {
  if (!('serviceWorker' in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      regs
        .filter((r) => {
          const script = r.active?.scriptURL || r.waiting?.scriptURL || r.installing?.scriptURL || '';
          return script.includes('crowd-count-asset-sw');
        })
        .map((r) => r.unregister()),
    );
  } catch {
    // ignore
  }
};
